import React, { useState } from 'react'
import { Row, Col, Label, Badge, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap'
import { useQuery } from '@apollo/client'
import gql from 'graphql-tag'
import Skeleton from 'react-loading-skeleton'
import 'react-loading-skeleton/dist/skeleton.css'
import DatetimeRenderer from '../../Material/DatetimeRenderer'
import toTitleCase from '../../Functions/toTitleCase'
import SubrogationClaimInfo from './SubrogationClaimInfo'
import LiabilityClaimInfo from './LiabilityClaimInfo'
import AddSubrogationClaimModal from '../IncidentModals/AddSubrogationClaimModal'
import AddLiabilityClaimModal from '../IncidentModals/AddLiabilityClaimModal'
import HistoryTab from '../HistoryTab/HistoryTab'
import DocumentsTab from '../DocumentsTab/DocumentsTab'
import { HasPermissionsQuery } from '../../Functions/querys'
import withApolloClient from "../../withApolloClient"

const AllSubrogationClaims = gql`
  query AllSubrogationClaims($incidentId: ID!) {
    allSubrogationClaims(incidentId: $incidentId, orderBy: ["-date_added"]) {
      edges {
        node {
          id
          dateAdded
          party { id partyName }
          status
          tpa { id name }
          tpaFee
          estimatedRepair
          estimatedAmount
          requestedAmount
          collectedAmount
          diminishedValue
          sentDate
          filedDate
          dailyRate
          daysDown
          notes
          lossOfUse
          history {
            date
            user
            comment
          }
        }
      }
    }
  }
`

const AllLiabilityClaims = gql`
  query AllLiabilityClaims($incidentId: ID!) {
    allLiabilityClaims(incidentId: $incidentId, orderBy: ["-date_added"]) {
      edges {
        node {
          id
          dateAdded
          externalId
          status
          tpa { id name }
          filedDate
          acknowledgmentDate
          reservedAmount
          paidAmount
          inLitigation
          notes
          adjuster{
            id
            name
            phone
            email
          }
          estimatedAmount
          claimAmount
          history {
            date
            user
            comment
          }
        }
      }
    }
  }
`

const Incident = gql`
  query Incident($incidentId: ID!) {
    incident(incidentId: $incidentId) {
      id
      incidentDate
    }
  }
`

const ClaimsTab = ({ id }) => {
    const { data: permissionsData } = useQuery(HasPermissionsQuery, {
        variables: { userPermissions: ["add_subrogationclaims", "add_liabilityclaim"] },
    })

    const hasPermissions = permissionsData?.hasPermissions || []

    const { data: claimsData, loading: claimsLoading, refetch: claimsRefetch } = useQuery(AllSubrogationClaims, {
        variables: { incidentId: id },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    })

    const { data: liabilityData, loading: liabilityLoading, refetch: liabilityRefetch } = useQuery(AllLiabilityClaims, {
        variables: { incidentId: id },
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    })

    const { data: incidentData, loading: incidentLoading } = useQuery(Incident, { variables: { incidentId: id } })

    const [openClaimId, setOpenClaimId] = useState(null)
    const [openLiabilityClaimId, setOpenLiabilityClaimId] = useState(null)
    const [openModal, setOpenModal] = useState(null)
    const [addClaimDropdownOpen, setAddClaimDropdownOpen] = useState(false)

    const [activeLiabilityTab, setActiveLiabilityTab] = useState({})
    const [activeSubroTab, setActiveSubroTab] = useState({})

    const claims = claimsData?.allSubrogationClaims?.edges?.map(edge => edge.node) || []
    const liabilityClaims = liabilityData?.allLiabilityClaims?.edges?.map(edge => edge.node) || []

    return (
        <div className="overview-tab-container">
            {openModal === "subrogation" &&
                <AddSubrogationClaimModal
                    isOpen={openModal === 'subrogation'}
                    toggle={() => setOpenModal(null)}
                    incidentId={id}
                    onSuccess={claimsRefetch}
                />
            }
            {openModal === "liability" && <AddLiabilityClaimModal
                isOpen={openModal === 'liability'}
                toggle={() => setOpenModal(null)}
                incidentId={id}
                onSuccess={liabilityRefetch}
            />
            }

            <div className='d-flex justify-content-end align-items-center mb-2'>
                <Dropdown isOpen={addClaimDropdownOpen} toggle={() => setAddClaimDropdownOpen(!addClaimDropdownOpen)} >
                    <DropdownToggle caret className="btn btn-primary">Add Claim</DropdownToggle>
                    <DropdownMenu end>
                        <DropdownItem onClick={() => { setOpenModal('liability'); setAddClaimDropdownOpen(false) }} disabled={!hasPermissions?.includes("add_liabilityclaim") || (!incidentLoading && !incidentData?.incident?.incidentDate)}>
                            Add Liability Claim
                        </DropdownItem>
                        <DropdownItem onClick={() => { setOpenModal('subrogation'); setAddClaimDropdownOpen(false) }} disabled={!hasPermissions?.includes("add_subrogationclaims") || (!incidentLoading && !incidentData?.incident?.incidentDate)}>
                            Add Subrogation Claim
                        </DropdownItem>
                    </DropdownMenu>
                </Dropdown>
            </div>
            {((liabilityClaims.length === 0 && claims.length === 0) || (liabilityLoading || claimsLoading)) &&
                <div className='info-container mb-4'>
                    {(liabilityLoading || claimsLoading) ? <Skeleton count={5} height={30} /> : (
                        (liabilityClaims.length === 0 && claims.length === 0) && <p className="text-danger mb-0">No claims added.</p>
                    )}
                </div>}
            {liabilityClaims.length > 0 && <>
                <h4>Liability Claims</h4>
                <div className='info-container mb-4' style={{ maxHeight: (openLiabilityClaimId || openClaimId) ? '900px' : '320px', overflowY: 'auto' }}>
                    <Row>
                        <Col xs={1}><Label style={{ fontWeight: 600 }}>#</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>Date Added</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>TPA (Third Party Administrator)</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>Adjuster</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>Status</Label></Col>
                        <Col xs={1} style={{ textAlign: 'center' }}><Label style={{ fontWeight: 600 }}>Action</Label></Col>
                    </Row>
                    <hr />
                    {liabilityClaims.map((claim, idx) => {
                        const claimOpen = openLiabilityClaimId === claim.id
                        const tabKey = `liabilityTab_${claim.id}`
                        const activeTab = activeLiabilityTab[tabKey] || 'details'
                        const historyTabKey = `liabilityHistoryTab_${claim.id}`
                        const activeHistoryTab = activeLiabilityTab[historyTabKey] || 'claimHistory'

                        const setTab = (tab) => {
                            setActiveLiabilityTab((prev) => ({ ...prev, [tabKey]: tab }))
                        }

                        const setHistoryTab = (tab) => {
                            setActiveLiabilityTab((prev) => ({ ...prev, [historyTabKey]: tab }))
                        }

                        return (
                            <div key={claim.id}>
                                <Row>
                                    <Col xs={1}><p>{idx + 1}</p></Col>
                                    <Col><p>{claim.dateAdded ? <DatetimeRenderer datetime={claim.dateAdded} /> : '-'}</p></Col>
                                    <Col><p>{claim.tpa?.name || '-'}</p></Col>
                                    <Col><p>{claim.adjuster?.name || '-'}</p></Col>
                                    <Col><p><Badge className='p-2 text-uppercase' color={'primary'}>{toTitleCase(claim.status) || '-'}</Badge></p></Col>
                                    <Col xs={1} style={{ alignItems: 'center', cursor: 'pointer', textAlign: 'center' }}>
                                        <p>
                                            {claimOpen
                                                ? <i className="fa fa-chevron-up" onClick={() => setOpenLiabilityClaimId(null)} />
                                                : <i className="fa fa-chevron-down" onClick={() => setOpenLiabilityClaimId(claim.id)} />
                                            }
                                        </p>
                                    </Col>
                                </Row>

                                {claimOpen && (
                                    <div className='info-container mt-0' style={{ background: '#f9f9f9' }}>
                                        <Nav tabs className='mb-3'>
                                            <NavItem>
                                                <NavLink active={activeTab === 'details'} onClick={() => setTab('details')}>Details</NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink active={activeTab === 'documents'} onClick={() => setTab('documents')}>Documents</NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink active={activeTab === 'history'} onClick={() => setTab('history')}>History</NavLink>
                                            </NavItem>
                                        </Nav>
                                        <TabContent activeTab={activeTab}>
                                            <TabPane tabId="details">
                                                <LiabilityClaimInfo claim={claim} refetch={liabilityRefetch} incidentId={id} />
                                            </TabPane>
                                            <TabPane tabId="documents">
                                                <DocumentsTab
                                                    id={id}
                                                    slug={"LIABILITY_" + claim.id}
                                                    refetchQuery={liabilityRefetch}
                                                    viewType={"claims"}
                                                />
                                            </TabPane>
                                            <TabPane tabId="history">
                                                <div className='info-container mt-0'>
                                                    <Nav tabs className="mb-3">
                                                        <NavItem>
                                                            <NavLink active={activeHistoryTab === 'claimHistory'} onClick={() => setHistoryTab('claimHistory')}>Claim History</NavLink>
                                                        </NavItem>
                                                    </Nav>
                                                    <TabContent activeTab={activeHistoryTab}>
                                                        <TabPane tabId="claimHistory">
                                                            <HistoryTab history={claim.history} />
                                                        </TabPane>
                                                    </TabContent>
                                                </div>
                                            </TabPane>
                                        </TabContent>
                                    </div>
                                )}
                                <hr />
                            </div>
                        )
                    })}
                </div>
            </>}

            {claims.length > 0 && <>
                <h4>Subrogation Claims</h4>
                <div className='info-container' style={{ maxHeight: (openLiabilityClaimId || openClaimId) ? '900px' : '320px', overflowY: 'auto' }}>
                    <Row>
                        <Col xs={1}><Label style={{ fontWeight: 600 }}>#</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>Date Added</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>TPA (Third Party Administrator)</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>Party</Label></Col>
                        <Col><Label style={{ fontWeight: 600 }}>Status</Label></Col>
                        <Col xs={1} style={{ textAlign: 'center' }}><Label style={{ fontWeight: 600 }}>Action</Label></Col>
                    </Row>
                    <hr style={{ margin: '8px 0' }} />
                    {claims.map((claim, idx) => {
                        const claimOpen = openClaimId === claim.id
                        const tabKey = `subrogationTab_${claim.id}`
                        const activeTab = activeSubroTab[tabKey] || 'details'
                        const historyTabKey = `subrogationHistoryTab_${claim.id}`
                        const activeHistoryTab = activeSubroTab[historyTabKey] || 'claimHistory'

                        const setTab = (tab) => {
                            setActiveSubroTab((prev) => ({ ...prev, [tabKey]: tab }))
                        }

                        const setHistoryTab = (tab) => {
                            setActiveSubroTab((prev) => ({ ...prev, [historyTabKey]: tab }))
                        }

                        return (
                            <div key={claim.id}>
                                <Row>
                                    <Col xs={1}><p>{idx + 1}</p></Col>
                                    <Col><p>{claim.dateAdded ? <DatetimeRenderer datetime={claim.dateAdded} /> : '-'}</p></Col>
                                    <Col><p>{claim.tpa?.name || '-'}</p></Col>
                                    <Col><p>{claim.party?.partyName || '-'}</p></Col>
                                    <Col><p><Badge color={'primary'} className='p-2 text-uppercase'>{toTitleCase(claim.status) || '-'}</Badge></p></Col>
                                    <Col xs={1} style={{ alignItems: 'center', cursor: 'pointer', textAlign: 'center' }}>
                                        <p>
                                            {claimOpen
                                                ? <i className="fa fa-chevron-up" onClick={() => setOpenClaimId(null)} />
                                                : <i className="fa fa-chevron-down" onClick={() => setOpenClaimId(claim.id)} />
                                            }
                                        </p>
                                    </Col>
                                </Row>

                                {claimOpen && (
                                    <div className='info-container mt-0' style={{ background: '#f9f9f9' }}>
                                        <Nav tabs className='mb-3'>
                                            <NavItem>
                                                <NavLink active={activeTab === 'details'} onClick={() => setTab('details')}>Details</NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink active={activeTab === 'documents'} onClick={() => setTab('documents')}>Documents</NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink active={activeTab === 'history'} onClick={() => setTab('history')}>History</NavLink>
                                            </NavItem>
                                        </Nav>
                                        <TabContent activeTab={activeTab}>
                                            <TabPane tabId="details">
                                                <SubrogationClaimInfo claim={claim} refetch={claimsRefetch} incidentId={id} />
                                            </TabPane>
                                            <TabPane tabId="documents">
                                                <DocumentsTab
                                                    id={id}
                                                    slug={"SUBROGATION_" + claim.id}
                                                    refetchQuery={claimsRefetch}
                                                    viewType={"claims"}
                                                />
                                            </TabPane>
                                            <TabPane tabId="history">
                                                <div className='info-container mt-0'>
                                                    <Nav tabs className="mb-3">
                                                        <NavItem>
                                                            <NavLink active={activeHistoryTab === 'claimHistory'} onClick={() => setHistoryTab('claimHistory')}>Claim History</NavLink>
                                                        </NavItem>
                                                    </Nav>
                                                    <TabContent activeTab={activeHistoryTab}>
                                                        <TabPane tabId="claimHistory">
                                                            <HistoryTab history={claim.history} />
                                                        </TabPane>
                                                    </TabContent>
                                                </div>
                                            </TabPane>
                                        </TabContent>
                                    </div>
                                )}
                                <hr />
                            </div>
                        )
                    })}
                </div>
            </>}
        </div>
    )
}


export default withApolloClient(ClaimsTab)
